// ======================================================================
// FILE:
// src/modules/finance/transactions/payment_voucher/pages/PaymentVoucherCreatePage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Payment Voucher - Create
// ======================================================================

import React, {
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import Select
    from "react-select";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import paymentVoucherService
    from "../services/paymentVoucherService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// OPTIONS
// ======================================================================

const PAYEE_TYPE_OPTIONS = [
    {
        value: "BUSINESS_PARTNER",
        label: "Business Partner",
    },
    {
        value: "VENDOR",
        label: "Vendor",
    },
    {
        value: "CUSTOMER",
        label: "Customer",
    },
    {
        value: "EMPLOYEE",
        label: "Employee",
    },
    {
        value: "GOVERNMENT",
        label: "Government / Tax Authority",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


const PAYMENT_MODE_OPTIONS = [
    {
        value: "BANK_TRANSFER",
        label: "Bank Transfer",
    },
    {
        value: "NEFT",
        label: "NEFT",
    },
    {
        value: "RTGS",
        label: "RTGS",
    },
    {
        value: "IMPS",
        label: "IMPS",
    },
    {
        value: "CHEQUE",
        label: "Cheque",
    },
    {
        value: "CASH",
        label: "Cash",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


const ALLOCATION_TYPE_OPTIONS = [
    {
        value: "MANUAL",
        label: "Manual",
    },
    {
        value: "INVOICE",
        label: "Invoice",
    },
    {
        value: "ADVANCE",
        label: "Advance",
    },
    {
        value: "TAX",
        label: "Tax",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


// ======================================================================
// INITIAL STATE
// ======================================================================

const getInitialForm = () => ({
    company_id: null,
    company_option: null,

    voucher_date:
        new Date()
            .toISOString()
            .slice(0, 10),

    
    payment_account_id: null,

    payment_account_option: null,

    payee_type: "",

    payee_id: null,

    payee_option: null,

    payee_name: "",

    payment_mode: "",

    reference_no: "",

    reference_date: "",

    currency_id: null,

    currency_option: null,

    exchange_rate: "1",

    remarks: "",
});


const getInitialLine = (
    sequenceNo = 1
) => ({
    sequence_no: sequenceNo,

    account_id: null,
    account_option: null,

    allocation_type: "MANUAL",

    business_partner_id: null,
    vendor_id: null,
    customer_id: null,
    employee_id: null,

    transaction_amount: "",

    base_amount: "",

    cost_center_id: null,
    cost_center_option: null,

    project_id: null,
    project_option: null,

    department_id: null,

    description: "",

    source_type: "",
    source_id: null,
    source_reference: "",
});


// ======================================================================
// COMPONENT
// ======================================================================

export default function PaymentVoucherCreatePage() {

    const navigate =
        useNavigate();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        formData,
        setFormData
    ] = useState(
        getInitialForm()
    );


    const [
        lines,
        setLines
    ] = useState([
        getInitialLine(1),
    ]);


    const [
        saving,
        setSaving
    ] = useState(false);


    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    // ==================================================================
    // COMPANY LOOKUP
    // ==================================================================

    async function loadCompanies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );


            return (
                response || []
            ).map(
                company => ({
                    value:
                        company.id,

                    label:
                        company.company_name,

                    data:
                        company,
                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Company lookup failed:",
                error
            );

            return [];

        }

    }

    // ======================================================================
    // PAYMENT ACCOUNT LOOKUP
    // ======================================================================

    async function loadPaymentAccounts(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchPaymentAccounts(
                        formData.company_id,
                        inputValue,
                        20
                    );


            return (
                    response || []
                ).map(
                    account => ({

                        ...account,

                        value:
                            account.id,

                        label:
                            `${account.account_name || "Unnamed Account"}`
                            + (
                                account.account_number
                                    ? ` - ${account.account_number}`
                                    : ""
                            )
                            + (
                                account.branch_name
                                    ? ` (${account.branch_name})`
                                    : ""
                            ),

                    })
                );

        }
        catch (
            error
        ) {

            console.error(
                "Payment Account lookup failed:",
                error
            );

            return [];

        }

    }
    // ======================================================================
// VENDOR LOOKUP
// ======================================================================

async function loadVendors(
    inputValue = ""
) {

    try {

        const response =
            await financeLookupService
                .searchVendors(
                    inputValue
                );

        return (
            response || []
        ).map(
            vendor => ({

                value:
                    vendor.id,

                label:
                    `${vendor.bp_code || ""} - ${vendor.bp_name || ""}`
                        .replace(
                            /^ - /,
                            ""
                        ),

                data:
                    vendor,

            })
        );

    }
    catch (
        error
    ) {

        console.error(
            "Vendor lookup failed:",
            error
        );

        return [];

    }

}


// ======================================================================
// CUSTOMER LOOKUP
// ======================================================================

async function loadCustomers(
    inputValue = ""
) {

    try {

        const response =
            await financeLookupService
                .searchCustomers(
                    inputValue
                );

        return (
            response || []
        ).map(
            customer => ({

                value:
                    customer.id,

                label:
                    `${customer.bp_code || ""} - ${customer.bp_name || ""}`
                        .replace(
                            /^ - /,
                            ""
                        ),

                data:
                    customer,

            })
        );

    }
    catch (
        error
    ) {

        console.error(
            "Customer lookup failed:",
            error
        );

        return [];

    }

}    
    // ==================================================================
    // CURRENCY LOOKUP
    // ==================================================================

    async function loadCurrencies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCurrencies(
                        inputValue
                    );


            return (
                response || []
            ).map(
                currency => ({

                    value:
                        currency.id ||
                        currency.value,

                    label:
                        currency.currency_name ||
                        currency.currency_code ||
                        currency.label,

                    data:
                        currency,

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Currency lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // ACCOUNT LOOKUP
    // ==================================================================

    async function loadAccounts(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchChartOfAccounts(
                        formData.company_id,
                        inputValue,
                        null,
                        20
                    );


            return response || [];

        }
        catch (
            error
        ) {

            console.error(
                "Account lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // COST CENTRE LOOKUP
    // ==================================================================

    async function loadCostCenters(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchCostCenters(
                        formData.company_id,
                        inputValue,
                        20
                    );


            return response || [];

        }
        catch (
            error
        ) {

            console.error(
                "Cost centre lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // HEADER CHANGE
    // ==================================================================

    function handleFieldChange(
    field,
    value,
    option = null
) {

    setFormData(
        previous => {

            const updated = {

                ...previous,

                [field]:
                    value,

            };

            if (
                field ===
                "payee_type"
            ) {

                updated.payee_id =
                    null;

                updated.payee_option =
                    null;

                updated.payee_name =
                    "";

            }
            if (
                field ===
                "payee_id"
            ) {

                updated.payee_option =
                    option;

                updated.payee_name =
                    option?.data?.bp_name ||
                    option?.label ||
                    "";

            }
            
            // ----------------------------------------------------------
            // COMPANY
            // ----------------------------------------------------------

            if (
                field ===
                "company_id"
            ) {

                updated.company_option =
                    option;


                // Payment Account belongs
                // to Company.

                updated.payment_account_id =
                    null;

                updated.payment_account_option =
                    null;

            }


            // ----------------------------------------------------------
            // PAYMENT ACCOUNT
            // ----------------------------------------------------------

            if (
                field ===
                "payment_account_id"
            ) {

                updated.payment_account_option =
                    option;

            }


            // ----------------------------------------------------------
            // CURRENCY
            // ----------------------------------------------------------

            if (
                field ===
                "currency_id"
            ) {

                updated.currency_option =
                    option;

            }


            return updated;

        }
    );


    setErrorMessage("");

}


    // ==================================================================
    // LINE CHANGE
    // ==================================================================

    function handleLineChange(
        index,
        field,
        value,
        option = null
    ) {

        setLines(
            previous => {

                const updated =
                    [...previous];


                updated[index] = {
                    ...updated[index],

                    [field]:
                        value,
                };


                if (
                    field ===
                    "account_id"
                ) {

                    updated[index]
                        .account_option =
                            option;

                }


                if (
                    field ===
                    "cost_center_id"
                ) {

                    updated[index]
                        .cost_center_option =
                            option;

                }


                return updated;

            }
        );


        setErrorMessage("");

    }


    // ==================================================================
    // ADD LINE
    // ==================================================================

    function addLine() {

        setLines(
            previous => [

                ...previous,

                getInitialLine(
                    previous.length + 1
                ),

            ]
        );

    }


    // ==================================================================
    // REMOVE LINE
    // ==================================================================

    function removeLine(
        index
    ) {

        if (
            lines.length <= 1
        ) {

            return;

        }


        setLines(
            previous => {

                const updated =
                    previous.filter(
                        (
                            _line,
                            lineIndex
                        ) =>
                            lineIndex !==
                            index
                    );


                return updated.map(
                    (
                        line,
                        lineIndex
                    ) => ({

                        ...line,

                        sequence_no:
                            lineIndex + 1,

                    })
                );

            }
        );

    }


    // ==================================================================
    // TOTALS
    // ==================================================================

    const totalAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.transaction_amount
                            ) || 0
                        ),
                    0
                ),
            [lines]
        );


    const totalBaseAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.base_amount
                            ) ||
                            (
                                Number(
                                    line.transaction_amount
                                ) *
                                (
                                    Number(
                                        formData.exchange_rate
                                    ) || 1
                                )
                            ) ||
                            0
                        ),
                    0
                ),
            [
                lines,
                formData.exchange_rate,
            ]
        );


    // ==================================================================
    // VALIDATION
    // ==================================================================

    function validateForm() {

        const errors = [];


        if (
            !formData.company_id
        ) {

            errors.push(
                "Company is required."
            );

        }


        if (
            !formData.voucher_date
        ) {

            errors.push(
                "Voucher Date is required."
            );

        }


        if (
            !formData.payment_account_id
        ) {

            errors.push(
                "Payment Account is required."
            );

        }


        if (
            !formData.payee_type
        ) {

            errors.push(
                "Payee Type is required."
            );

        }


        if (
            !formData.payment_mode
        ) {

            errors.push(
                "Payment Mode is required."
            );

        }


        if (
            !formData.currency_id
        ) {

            errors.push(
                "Currency is required."
            );

        }


        if (
            Number(
                formData.exchange_rate
            ) <= 0
        ) {

            errors.push(
                "Exchange Rate must be greater than zero."
            );

        }


        if (
            !lines.length
        ) {

            errors.push(
                "At least one allocation line is required."
            );

        }


        lines.forEach(
            (
                line,
                index
            ) => {

                if (
                    !line.account_id
                ) {

                    errors.push(
                        `GL Account is required for line ${index + 1}.`
                    );

                }


                if (
                    Number(
                        line.transaction_amount
                    ) <= 0
                ) {

                    errors.push(
                        `Amount is required for line ${index + 1}.`
                    );

                }

            }
        );


        return errors;

    }


    // ==================================================================
    // BUILD PAYLOAD
    // ==================================================================

    function buildPayload() {

        const exchangeRate =
            Number(
                formData.exchange_rate
            ) || 1;


        return {

            company_id:
                Number(
                    formData.company_id
                ),

            voucher_date:
                formData.voucher_date,

            payment_account_id:
                Number(
                    formData.payment_account_id
                ),

            payee_type:
                formData.payee_type,

            payee_id:
                formData.payee_id
                    ? Number(
                        formData.payee_id
                    )
                    : null,

            payee_name:
                formData.payee_name
                    ?.trim() ||
                null,

            payment_mode:
                formData.payment_mode,

            reference_no:
                formData.reference_no
                    ?.trim() ||
                null,

            reference_date:
                formData.reference_date ||
                null,

            currency_id:
                Number(
                    formData.currency_id
                ),

            exchange_rate:
                exchangeRate,

            remarks:
                formData.remarks
                    ?.trim() ||
                null,

            lines:
                lines.map(
                    (
                        line,
                        index
                    ) => {

                        const amount =
                            Number(
                                line.transaction_amount
                            ) || 0;


                        return {

                            sequence_no:
                                index + 1,

                            account_id:
                                Number(
                                    line.account_id
                                ),

                            allocation_type:
                                line.allocation_type ||
                                "MANUAL",

                            business_partner_id:
                                line.business_partner_id
                                    ? Number(
                                        line.business_partner_id
                                    )
                                    : null,

                            vendor_id:
                                line.vendor_id
                                    ? Number(
                                        line.vendor_id
                                    )
                                    : null,

                            customer_id:
                                line.customer_id
                                    ? Number(
                                        line.customer_id
                                    )
                                    : null,

                            employee_id:
                                line.employee_id
                                    ? Number(
                                        line.employee_id
                                    )
                                    : null,

                            transaction_amount:
                                amount,

                            base_amount:
                                Number(
                                    line.base_amount
                                ) ||
                                (
                                    amount *
                                    exchangeRate
                                ),

                            cost_center_id:
                                line.cost_center_id
                                    ? Number(
                                        line.cost_center_id
                                    )
                                    : null,

                            project_id:
                                line.project_id
                                    ? Number(
                                        line.project_id
                                    )
                                    : null,

                            department_id:
                                line.department_id
                                    ? Number(
                                        line.department_id
                                    )
                                    : null,

                            description:
                                line.description
                                    ?.trim() ||
                                null,

                            source_type:
                                line.source_type
                                    ?.trim() ||
                                null,

                            source_id:
                                line.source_id
                                    ? Number(
                                        line.source_id
                                    )
                                    : null,

                            source_reference:
                                line.source_reference
                                    ?.trim() ||
                                null,

                        };

                    }
                ),

        };

    }


    // ==================================================================
    // SAVE
    // ==================================================================

    async function handleSaveDraft() {

        const errors =
            validateForm();


        if (
            errors.length
        ) {

            setErrorMessage(
                errors.join(" ")
            );

            return;

        }


        try {

            setSaving(
                true
            );

            setErrorMessage("");


            const payload =
                buildPayload();


            const response =
                await paymentVoucherService
                    .createPaymentVoucher(
                        payload
                    );


            navigate(
                `/finance/payment-vouchers/view/${response.id}`
            );

        }
        catch (
            error
        ) {

            console.error(
                "Payment Voucher create failed:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to create Payment Voucher."
            );

        }
        finally {

            setSaving(
                false
            );

        }

    }


    // ==================================================================
    // CANCEL
    // ==================================================================

    function handleCancel() {

        navigate(
            "/finance/payment-vouchers"
        );

    }


    // ==================================================================
    // FORMAT
    // ==================================================================

    function formatAmount(
        value
    ) {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        );

    }


    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <>

            <FinancePageHeader

                icon="fas fa-money-check-alt"

                title="New Payment Voucher"

                subtitle={
                    "Create a Finance Payment Voucher."
                }

                breadcrumbs={[

                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Payment Voucher",

                        to:
                            "/finance/payment-vouchers",
                    },

                    {
                        label:
                            "New",
                    },

                ]}

                actions={

                    <div
                        className="
                            d-flex
                            gap-2
                        "
                    >

                        <button
                            type="button"
                            className="
                                btn
                                btn-outline-secondary
                            "
                            onClick={
                                handleCancel
                            }
                            disabled={
                                saving
                            }
                        >
                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />
                            Back to List
                        </button>


                        <button
                            type="button"
                            className="
                                btn
                                btn-primary
                            "
                            onClick={
                                handleSaveDraft
                            }
                            disabled={
                                saving
                            }
                        >
                            <i
                                className="
                                    fas
                                    fa-save
                                    me-1
                                "
                            />

                            {
                                saving
                                    ? "Saving..."
                                    : "Save Draft"
                            }

                        </button>

                    </div>

                }

            />


            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                VOUCHER DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-body"
                >

                    <h5
                        className="
                            text-primary
                            border-bottom
                            pb-2
                            mb-4
                        "
                    >
                        Voucher Details
                    </h5>


                    <div
                        className="row g-3"
                    >

                        {/* COMPANY */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Company
                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>
                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCompanies
                                }

                                isClearable

                                isDisabled={
                                    saving
                                }

                                value={
                                    formData.company_option
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "company_id",
                                            option?.value ||
                                            null,
                                            option
                                        )
                                }

                                placeholder="Select Company..."

                            />

                        </div>


                        {/* VOUCHER DATE */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Voucher Date
                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>
                            </label>


                            <input

                                type="date"

                                className="form-control"

                                value={
                                    formData.voucher_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "voucher_date",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* PAYEE TYPE */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Payee Type
                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>
                            </label>


                            <Select

                                options={
                                    PAYEE_TYPE_OPTIONS
                                }

                                isClearable

                                isDisabled={
                                    saving
                                }

                                value={
                                    PAYEE_TYPE_OPTIONS.find(
                                        option =>
                                            option.value ===
                                            formData.payee_type
                                    ) ||
                                    null
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "payee_type",
                                            option?.value ||
                                            ""
                                        )
                                }

                                placeholder="Select Payee Type..."

                            />

                        </div>


                        {/* PAYEE */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Payee
                            </label>


                            {
                                formData.payee_type === "OTHER" ? (

                                    <input

                                        type="text"

                                        className="form-control"

                                        value={
                                            formData.payee_name
                                        }

                                        onChange={
                                            event =>
                                                handleFieldChange(
                                                    "payee_name",
                                                    event.target.value
                                                )
                                        }

                                        placeholder="Enter Payee..."

                                        disabled={
                                            saving
                                        }

                                    />

                                ) : (

                                    <AsyncSelect

                                        cacheOptions

                                        defaultOptions

                                        loadOptions={
                                            formData.payee_type === "VENDOR"
                                                ? loadVendors
                                                : formData.payee_type === "CUSTOMER"
                                                    ? loadCustomers
                                                    : () => []
                                        }

                                        isClearable

                                        isDisabled={
                                            !formData.company_id ||
                                            !formData.payee_type ||
                                            saving
                                        }

                                        value={
                                            formData.payee_option
                                        }

                                        onChange={
                                            option => {

                                                handleFieldChange(
                                                    "payee_id",
                                                    option?.value ||
                                                    null,
                                                    option
                                                );

                                            }
                                        }

                                        placeholder={
                                            !formData.payee_type
                                                ? "Select Payee Type First..."
                                                : formData.payee_type === "VENDOR"
                                                    ? "Search Vendor..."
                                                    : formData.payee_type === "CUSTOMER"
                                                        ? "Search Customer..."
                                                        : "Payee lookup not configured yet..."
                                        }

                                        menuPortalTarget={
                                            document.body
                                        }

                                        styles={{
                                            menuPortal: base => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                        }}

                                    />

                                )
                            }

                        </div>


                        {/* PAYMENT MODE */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Payment Mode
                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>
                            </label>


                            <Select

                                options={
                                    PAYMENT_MODE_OPTIONS
                                }

                                isClearable

                                isDisabled={
                                    saving
                                }

                                value={
                                    PAYMENT_MODE_OPTIONS.find(
                                        option =>
                                            option.value ===
                                            formData.payment_mode
                                    ) ||
                                    null
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "payment_mode",
                                            option?.value ||
                                            ""
                                        )
                                }

                                placeholder="Select Payment Mode..."

                            />

                        </div>


                        {/* PAYMENT ACCOUNT */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Payment Account
                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>
                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadPaymentAccounts
                                }

                                isClearable

                                isDisabled={
                                    !formData.company_id ||
                                    saving
                                }

                                value={
                                    formData.payment_account_option
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "payment_account_id",
                                            option?.value ||
                                            null,
                                            option
                                        )
                                }

                                placeholder={
                                    formData.company_id
                                        ? "Search Payment Account..."
                                        : "Select Company First..."
                                }

                                getOptionLabel={
                                    option => {

                                        const accountName =
                                            option.account_name ||
                                            "Unnamed Account";

                                        const accountNumber =
                                            option.account_number
                                                ? ` - ${option.account_number}`
                                                : "";

                                        const branch =
                                            option.branch_name
                                                ? ` (${option.branch_name})`
                                                : "";

                                        return (
                                            `${accountName}${accountNumber}${branch}`
                                        );

                                    }
                                }

                                getOptionValue={
                                    option =>
                                        String(
                                            option.id ||
                                            option.value
                                        )
                                }

                            />

                        </div>


                        {/* CURRENCY */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Currency
                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>
                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCurrencies
                                }

                                isClearable

                                isDisabled={
                                    saving
                                }

                                value={
                                    formData.currency_option
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "currency_id",
                                            option?.value ||
                                            null,
                                            option
                                        )
                                }

                                placeholder="Select Currency..."

                            />

                        </div>


                        {/* EXCHANGE RATE */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Exchange Rate
                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>
                            </label>


                            <input

                                type="number"

                                min="0.00000001"

                                step="0.00000001"

                                className="form-control"

                                value={
                                    formData.exchange_rate
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "exchange_rate",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* REFERENCE NO */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference No.
                            </label>


                            <input

                                type="text"

                                className="form-control"

                                value={
                                    formData.reference_no
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_no",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* REFERENCE DATE */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference Date
                            </label>


                            <input

                                type="date"

                                className="form-control"

                                value={
                                    formData.reference_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_date",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* REMARKS */}

                        <div
                            className="
                                col-lg-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Remarks
                            </label>


                            <textarea

                                className="form-control"

                                rows="2"

                                value={
                                    formData.remarks
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "remarks",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                ALLOCATION DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-body"
                >

                    <div
                        className="
                            d-flex
                            justify-content-between
                            align-items-center
                            mb-3
                        "
                    >

                        <h5
                            className="
                                text-primary
                                mb-0
                            "
                        >
                            Allocation Details
                        </h5>


                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-primary
                                btn-sm
                            "

                            onClick={
                                addLine
                            }

                            disabled={
                                saving
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-plus
                                    me-1
                                "
                            />

                            Add Line

                        </button>

                    </div>


                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-bordered
                                align-middle
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th>
                                        #
                                    </th>

                                    <th
                                        style={{
                                            minWidth:
                                                240,
                                        }}
                                    >
                                        GL Account
                                        <span
                                            className="text-danger"
                                        >
                                            {" *"}
                                        </span>
                                    </th>

                                    <th
                                        style={{
                                            minWidth:
                                                150,
                                        }}
                                    >
                                        Allocation
                                    </th>

                                    <th
                                        style={{
                                            minWidth:
                                                140,
                                        }}
                                    >
                                        Amount
                                        <span
                                            className="text-danger"
                                        >
                                            {" *"}
                                        </span>
                                    </th>

                                    <th
                                        style={{
                                            minWidth:
                                                200,
                                        }}
                                    >
                                        Cost Centre
                                    </th>

                                    <th
                                        style={{
                                            minWidth:
                                                220,
                                        }}
                                    >
                                        Description
                                    </th>

                                    <th>
                                        Action
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    lines.map(
                                        (
                                            line,
                                            index
                                        ) => (

                                            <tr
                                                key={
                                                    index
                                                }
                                            >

                                                <td>
                                                    {
                                                        index + 1
                                                    }
                                                </td>


                                                {/* ACCOUNT */}

                                                <td>

                                                    <AsyncSelect

                                                        cacheOptions

                                                        defaultOptions

                                                        loadOptions={
                                                            loadAccounts
                                                        }

                                                        isClearable

                                                        isDisabled={
                                                            !formData.company_id ||
                                                            saving
                                                        }

                                                        value={
                                                            line.account_option
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "account_id",
                                                                    option?.value ||
                                                                    null,
                                                                    option
                                                                )
                                                        }

                                                        placeholder="Select Account..."

                                                    menuPortalTarget={
                                                            document.body
                                                        }

                                                        styles={{
                                                            menuPortal: base => ({
                                                                ...base,
                                                                zIndex: 9999,
                                                            }),
                                                        }}
                                                    />

                                                </td>


                                                {/* ALLOCATION */}

                                                <td>

                                                    <Select

                                                        options={
                                                            ALLOCATION_TYPE_OPTIONS
                                                        }

                                                        value={
                                                            ALLOCATION_TYPE_OPTIONS.find(
                                                                option =>
                                                                    option.value ===
                                                                    line.allocation_type
                                                            )
                                                        }

                                                        isDisabled={
                                                            saving
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "allocation_type",
                                                                    option?.value ||
                                                                    "MANUAL"
                                                                )
                                                        }

                                                    menuPortalTarget={
                                                            document.body
                                                        }

                                                        styles={{
                                                            menuPortal: base => ({
                                                                ...base,
                                                                zIndex: 9999,
                                                            }),
                                                        }}
                                                    
                                                    />

                                                </td>


                                                {/* AMOUNT */}

                                                <td>

                                                    <input

                                                        type="number"

                                                        min="0"

                                                        step="0.01"

                                                        className="form-control"

                                                        value={
                                                            line.transaction_amount
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "transaction_amount",
                                                                    event.target.value
                                                                )
                                                        }

                                                        disabled={
                                                            saving
                                                        }

                                                        placeholder="0.00"

                                                    />

                                                </td>


                                                {/* COST CENTRE */}

                                                <td>

                                                    <AsyncSelect

                                                        cacheOptions

                                                        defaultOptions

                                                        loadOptions={
                                                            loadCostCenters
                                                        }

                                                        isClearable

                                                        isDisabled={
                                                            !formData.company_id ||
                                                            saving
                                                        }

                                                        value={
                                                            line.cost_center_option
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "cost_center_id",
                                                                    option?.value ||
                                                                    null,
                                                                    option
                                                                )
                                                        }

                                                        placeholder="Select Cost Centre..."

                                                                                                                menuPortalTarget={
                                                                document.body
                                                            }

                                                            styles={{
                                                                menuPortal: base => ({
                                                                    ...base,
                                                                    zIndex: 9999,
                                                                }),
                                                            }}
                                                    />

                                                </td>


                                                {/* DESCRIPTION */}

                                                <td>

                                                    <input

                                                        type="text"

                                                        className="form-control"

                                                        value={
                                                            line.description
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "description",
                                                                    event.target.value
                                                                )
                                                        }

                                                        disabled={
                                                            saving
                                                        }

                                                        placeholder="Description"

                                                    />

                                                </td>


                                                {/* DELETE */}

                                                <td
                                                    className="text-center"
                                                >

                                                    <button

                                                        type="button"

                                                        className="
                                                            btn
                                                            btn-outline-danger
                                                            btn-sm
                                                        "

                                                        onClick={() =>
                                                            removeLine(
                                                                index
                                                            )
                                                        }

                                                        disabled={
                                                            saving ||
                                                            lines.length <= 1
                                                        }

                                                    >

                                                        <i
                                                            className="
                                                                fas
                                                                fa-trash
                                                            "
                                                        />

                                                    </button>

                                                </td>

                                            </tr>

                                        )
                                    )
                                }

                            </tbody>

                        </table>

                    </div>


                    {/* TOTAL */}

                    <div
                        className="
                            d-flex
                            justify-content-end
                            mt-3
                        "
                    >

                        <div
                            className="
                                border
                                rounded
                                p-3
                                bg-light
                            "
                            style={{
                                minWidth:
                                    350,
                            }}
                        >

                            <div
                                className="
                                    d-flex
                                    justify-content-between
                                "
                            >

                                <strong>
                                    Total Payment
                                </strong>

                                <strong
                                    className="
                                        text-primary
                                    "
                                >
                                    {
                                        formatAmount(
                                            totalAmount
                                        )
                                    }
                                </strong>

                            </div>


                            <div
                                className="
                                    d-flex
                                    justify-content-between
                                    text-muted
                                    mt-1
                                "
                            >

                                <span>
                                    Base Amount
                                </span>

                                <span>
                                    {
                                        formatAmount(
                                            totalBaseAmount
                                        )
                                    }
                                </span>

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                FOOTER
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                    mb-4
                "
            >

                <div
                    className="
                        card-body
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <div>

                        <span
                            className="text-danger"
                        >
                            *
                        </span>

                        {" "}
                        Mandatory Fields

                    </div>


                    <div
                        className="
                            d-flex
                            gap-2
                        "
                    >

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleCancel
                            }

                            disabled={
                                saving
                            }

                        >
                            Cancel
                        </button>


                        <button

                            type="button"

                            className="
                                btn
                                btn-primary
                            "

                            onClick={
                                handleSaveDraft
                            }

                            disabled={
                                saving
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-save
                                    me-1
                                "
                            />

                            {
                                saving
                                    ? "Saving..."
                                    : "Save Draft"
                            }

                        </button>

                    </div>

                </div>

            </div>

        </>

    );

}